import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { isEmailVerified as resolveEmailVerified } from '../lib/emailVerificationFlow';

const AuthContext = createContext(null);

function getStoredUser() {
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  if (!token || !storedUser) return null;

  try {
    return JSON.parse(storedUser);
  } catch (error) {
    localStorage.removeItem('user');
    return null;
  }
}

export function AuthProvider({ children }) {
  // Hydrate immediately from localStorage so private preview mode does not
  // briefly treat valid OWNER/USER sessions as anonymous while /auth/me loads.
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  const [sessionRevision, setSessionRevision] = useState(0);

  const bumpSessionRevision = useCallback(() => {
    setSessionRevision((n) => n + 1);
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('user');
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await authAPI.login({ email, password, remember_me: rememberMe });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    setUser(userData);
    bumpSessionRevision();
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    bumpSessionRevision();
    return userData;
  };

  const logout = () => {
    // Fire server-side revocation before removing the token so the request
    // interceptor can still attach the Authorization header. Local logout is
    // not blocked on this call - a network failure must never trap the user.
    authAPI.logout().catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberedEmail');
    setUser(null);
    bumpSessionRevision();
  };

  const roleUpper = String(user?.role || '').toUpperCase();
  const isOwner = roleUpper === 'OWNER' || roleUpper === 'ADMIN';
  const isAdmin = roleUpper === 'ADMIN';
  const isEmailVerified = resolveEmailVerified(user);
  const token = localStorage.getItem('token');

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      isOwner, 
      isAdmin,
      isEmailVerified,
      isAuthenticated: !!user,
      token,
      sessionRevision,
      bumpSessionRevision,
      // Re-fetch /auth/me to pick up email_verified after verification.
      refreshUser: loadUser,
      reloadMe: loadUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}